import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Loader2 } from "lucide-react";

const TERMS_VERSION = "1.0";

export const TermsGate = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [needsAccept, setNeedsAccept] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      setChecking(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("legal_acceptances")
        .select("id")
        .eq("user_id", user.id)
        .eq("terms_version", TERMS_VERSION)
        .maybeSingle();
      setNeedsAccept(!data);
      setChecking(false);
    })();
  }, [user]);

  const accept = async () => {
    if (!user || !agreed) return;
    setSubmitting(true);
    const { error } = await supabase.from("legal_acceptances").insert({
      user_id: user.id,
      terms_version: TERMS_VERSION,
      user_agent: navigator.userAgent.slice(0, 500),
    });
    setSubmitting(false);
    if (!error) setNeedsAccept(false);
  };

  if (checking) return null;
  if (!needsAccept) return <>{children}</>;

  return (
    <>
      {children}
      <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-xl shadow-xl max-w-lg w-full p-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h2 className="font-display text-xl font-semibold">Before you formulate</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Glowie is an <strong className="text-foreground">educational AI tool</strong>. Output may
            contain errors. You are solely responsible for any product you make, apply, or sell.
          </p>
          <ul className="text-sm space-y-1.5 mb-5 list-disc pl-5 text-muted-foreground">
            <li>Not medical or professional advice.</li>
            <li>Always patch-test before use.</li>
            <li>You assume all risk for adverse reactions, contamination, and regulatory compliance.</li>
            <li>Affiliate links may earn us a commission at no cost to you.</li>
          </ul>
          <label className="flex items-start gap-2 cursor-pointer mb-4 select-none">
            <Checkbox
              checked={agreed}
              onCheckedChange={(c) => setAgreed(c === true)}
              className="mt-0.5"
            />
            <span className="text-sm">
              I have read and agree to the{" "}
              <Link to="/terms" target="_blank" className="underline font-medium">Terms of Service</Link> and{" "}
              <Link to="/disclaimer" target="_blank" className="underline font-medium">Safety Disclaimer</Link>.
            </span>
          </label>
          <Button onClick={accept} disabled={!agreed || submitting} className="w-full">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Accept &amp; continue
          </Button>
        </div>
      </div>
    </>
  );
};